import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { usersApi } from '../services/api/users';
import { User, UserCreate } from '../types/api';
import { Card, H1, H3, Text, Badge, Button, Spinner, Alert } from '../components/ui';
import { Input } from '../components/forms';
import { useToast } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { extractApiErrorMessage } from '../utils/apiError';

const createUserSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  email: z.string().email('Valid email is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  is_active: z.boolean().optional(),
  is_staff: z.boolean().optional(),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // All hook calls must happen before any conditional return -- React's
  // rules-of-hooks require the call order to be identical on every render.
  // The is_superuser guard runs at the bottom of the hook block, just
  // before the JSX so the redirect happens before any UI renders.
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { is_active: true, is_staff: false },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getUsers({ limit: 100 }),
    enabled: !!currentUser?.is_superuser,
  });

  const createMutation = useMutation({
    mutationFn: (data: UserCreate) => usersApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      addToast({ title: 'Success', message: 'User created successfully', type: 'success' });
      reset();
    },
    onError: (err: unknown) => {
      addToast({
        title: 'Error',
        message: extractApiErrorMessage(err, 'Failed to create user'),
        type: 'error',
      });
    },
  });

  // Guard: only superusers can access the users page. Sits AFTER the hook
  // calls above so the hook-call order stays stable across renders.
  if (!currentUser?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  const users = data?.items || [];
  const filteredUsers = search
    ? users.filter((u: User) =>
        `${u.first_name} ${u.last_name} ${u.email} ${u.username}`
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    : users;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load users. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <H1>Users</H1>
          <Text className="mt-2 text-gray-600">
            Manage teachers and staff accounts
          </Text>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-primary-600">{users.length}</div>
          <Text className="text-sm text-gray-500">Total Users</Text>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {users.filter((u: User) => u.is_active).length}
          </div>
          <Text className="text-sm text-gray-500">Active</Text>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {users.filter((u: User) => u.is_staff).length}
          </div>
          <Text className="text-sm text-gray-500">Staff</Text>
        </Card>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user: User) => (
            <Link key={user.id} to={`/users/${user.id}`}>
              <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-medium text-sm">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <Text className="font-medium">
                        {user.first_name} {user.last_name}
                      </Text>
                      <Text className="text-sm text-gray-500">{user.email}</Text>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {user.is_staff && <Badge variant="info" size="sm">Staff</Badge>}
                    <Badge variant={user.is_active ? 'success' : 'warning'} size="sm">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </Card>
            </Link>
          ))
        ) : (
          <Card className="p-8">
            <div className="text-center">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <H3 className="mt-4 text-gray-900">
                {search ? 'No Matching Users' : 'No Users Yet'}
              </H3>
              <Text className="mt-2 text-gray-500">
                {search ? 'Try a different search term.' : 'Create your first user to get started.'}
              </Text>
            </div>
          </Card>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50" onClick={() => setShowCreateModal(false)} />
            <Card className="relative z-10 max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <H3>Create User</H3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit((data) => createMutation.mutate(data as UserCreate))} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    {...register('first_name')}
                    label="First Name"
                    error={errors.first_name?.message}
                  />
                  <Input
                    {...register('last_name')}
                    label="Last Name"
                    error={errors.last_name?.message}
                  />
                </div>
                <Input
                  {...register('username')}
                  label="Username"
                  error={errors.username?.message}
                />
                <Input
                  {...register('email')}
                  label="Email"
                  type="email"
                  error={errors.email?.message}
                />
                <Input
                  {...register('password')}
                  label="Password"
                  type="password"
                  error={errors.password?.message}
                />
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input {...register('is_active')} type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input {...register('is_staff')} type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                    <span className="text-sm text-gray-700">Staff</span>
                  </label>
                </div>
                <div className="flex space-x-3 pt-2">
                  <Button type="submit" variant="primary" isLoading={createMutation.isPending}>
                    Create User
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); reset(); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
